import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { SyncButton } from '../../components/SyncButton';
import {
  getIntegrationsGooglesheets,
  postIntegrationsGooglesheets,
  deleteIntegrationsGooglesheetsIntegrationId,
  postIntegrationsGooglesheetsIntegrationIdTestConnection,
  postIntegrationsGooglesheetsIntegrationIdSync,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import { useNotifications } from '../../contexts/NotificationContext';

const GoogleSheetsTab: React.FC = () => {
  const [googleSheetsIntegrations, setGoogleSheetsIntegrations] = useState<any[]>([]);
  const [isLoadingGoogleSheetsIntegrations, setIsLoadingGoogleSheetsIntegrations] = useState(false);
  const [googleSheetsError, setGoogleSheetsError] = useState<string | null>(null);
  const [showAddGoogleSheetsIntegration, setShowAddGoogleSheetsIntegration] = useState(false);
  const [newGoogleSheetsName, setNewGoogleSheetsName] = useState('');
  const [newGoogleSheetsCredentials, setNewGoogleSheetsCredentials] = useState('');
  const [newGoogleSheetsSpreadsheetId, setNewGoogleSheetsSpreadsheetId] = useState('');
  const [newGoogleSheetsSheetName, setNewGoogleSheetsSheetName] = useState('');
  const [newGoogleSheetsType, setNewGoogleSheetsType] = useState<'schedule' | 'ar'>('schedule');
  const [newGoogleSheetsStartDate, setNewGoogleSheetsStartDate] = useState('');
  const [newGoogleSheetsEndDate, setNewGoogleSheetsEndDate] = useState('');
  const [showGoogleSheetsCredentials, setShowGoogleSheetsCredentials] = useState(false);
  const [isCreatingGoogleSheets, setIsCreatingGoogleSheets] = useState(false);

  // Get active syncs to monitor completion
  const { activeSyncs } = useNotifications();

  // Ref to track previous sync states
  const previousSyncStatesRef = useRef<Map<string, string>>(new Map());

  // Fetch integrations on mount
  useEffect(() => {
    fetchGoogleSheetsIntegrations();
  }, []);

  // Monitor sync completion and refetch integration data
  useEffect(() => {
    activeSyncs.forEach((syncEvent, syncId) => {
      const prevStatus = previousSyncStatesRef.current.get(syncId);
      const currentStatus = syncEvent.status;

      if ((prevStatus === 'started' || prevStatus === 'progress') &&
          (currentStatus === 'completed' || currentStatus === 'failed')) {

        const integrationId = syncEvent.integration_id;

        if (googleSheetsIntegrations.some(int => int.id === integrationId)) {
          console.log(`[GoogleSheetsTab] Google Sheets sync ${currentStatus} for ${integrationId}, refetching integrations`);
          fetchGoogleSheetsIntegrations();
        }
      }

      previousSyncStatesRef.current.set(syncId, currentStatus);
    });
  }, [activeSyncs, googleSheetsIntegrations]);

  const fetchGoogleSheetsIntegrations = async () => {
    setIsLoadingGoogleSheetsIntegrations(true);
    setGoogleSheetsError(null);
    try {
      const response = await getIntegrationsGooglesheets();
      setGoogleSheetsIntegrations(response.integrations || []);
    } catch (error) {
      console.error('Error fetching Google Sheets integrations:', error);
      setGoogleSheetsError('Failed to fetch Google Sheets integrations');
    } finally {
      setIsLoadingGoogleSheetsIntegrations(false);
    }
  };

  const handleCreateGoogleSheetsIntegration = async () => {
    setIsCreatingGoogleSheets(true);
    setGoogleSheetsError(null);
    try {
      if (!newGoogleSheetsName.trim()) {
        setGoogleSheetsError('Please enter a name for this integration');
        setIsCreatingGoogleSheets(false);
        return;
      }
      if (!newGoogleSheetsCredentials.trim()) {
        setGoogleSheetsError('Please enter service account credentials');
        setIsCreatingGoogleSheets(false);
        return;
      }
      if (!newGoogleSheetsSpreadsheetId.trim()) {
        setGoogleSheetsError('Please enter a spreadsheet ID');
        setIsCreatingGoogleSheets(false);
        return;
      }
      if (!newGoogleSheetsSheetName.trim()) {
        setGoogleSheetsError('Please enter a sheet name');
        setIsCreatingGoogleSheets(false);
        return;
      }
      if (!newGoogleSheetsStartDate || !newGoogleSheetsEndDate) {
        setGoogleSheetsError('Please enter both start and end dates');
        setIsCreatingGoogleSheets(false);
        return;
      }

      await postIntegrationsGooglesheets({
        name: newGoogleSheetsName,
        service_account_credentials: newGoogleSheetsCredentials,
        spreadsheet_id: newGoogleSheetsSpreadsheetId,
        sheet_name: newGoogleSheetsSheetName,
        type: newGoogleSheetsType,
        start_date: new Date(newGoogleSheetsStartDate).toISOString(),
        end_date: new Date(newGoogleSheetsEndDate).toISOString()
      });

      setShowAddGoogleSheetsIntegration(false);
      setNewGoogleSheetsName('');
      setNewGoogleSheetsCredentials('');
      setNewGoogleSheetsSpreadsheetId('');
      setNewGoogleSheetsSheetName('');
      setNewGoogleSheetsType('schedule');
      setNewGoogleSheetsStartDate('');
      setNewGoogleSheetsEndDate('');
      await fetchGoogleSheetsIntegrations();
    } catch (error: any) {
      console.error('Error creating Google Sheets integration:', error);
      if (error.response?.status === 400) {
        setGoogleSheetsError('Invalid input or name already exists');
      } else {
        setGoogleSheetsError('Failed to create Google Sheets integration');
      }
    } finally {
      setIsCreatingGoogleSheets(false);
    }
  };

  const handleDeleteGoogleSheetsIntegration = async (integrationId: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete the Google Sheets integration "${name}"?`)) {
      return;
    }

    try {
      await deleteIntegrationsGooglesheetsIntegrationId(integrationId);
      await fetchGoogleSheetsIntegrations();
    } catch (error) {
      console.error('Error deleting Google Sheets integration:', error);
      setGoogleSheetsError('Failed to delete Google Sheets integration');
    }
  };

  const handleTestGoogleSheetsConnection = async (integrationId: string, name: string) => {
    try {
      await postIntegrationsGooglesheetsIntegrationIdTestConnection(integrationId);
      await fetchGoogleSheetsIntegrations();
    } catch (error: any) {
      console.error('Error testing Google Sheets connection:', error);
      await fetchGoogleSheetsIntegrations();
    }
  };

  const handleSyncGoogleSheetsIntegration = async (integrationId: string, name: string): Promise<string | undefined> => {
    try {
      const resp = await postIntegrationsGooglesheetsIntegrationIdSync(integrationId);
      await fetchGoogleSheetsIntegrations();
      return (resp as any)?.sync_id;
    } catch (error: any) {
      console.error('Error triggering Google Sheets sync:', error);
      await fetchGoogleSheetsIntegrations();
      return undefined;
    }
  };

  return (
    <>
      <Card className="">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <div className="ms-4 me-4 bg-header-control rounded p-2">
                <i className="bi bi-file-earmark-spreadsheet text-success" style={{ fontSize: '4rem' }}></i>
              </div>
              <div>
                <h5 className="mb-1">Google Sheets Integrations</h5>
                <p className="text-muted mb-0">Connect Google Sheets for schedule and AR data</p>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={() => setShowAddGoogleSheetsIntegration(true)}
              disabled={isCreatingGoogleSheets}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Integration
            </Button>
          </div>

          {googleSheetsError && (
            <Alert variant="danger" dismissible onClose={() => setGoogleSheetsError(null)}>
              {googleSheetsError}
            </Alert>
          )}

          {isLoadingGoogleSheetsIntegrations ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading integrations...</p>
            </div>
          ) : googleSheetsIntegrations.length === 0 ? (
            <div className="text-center py-5">
              <div className="d-inline-block bg-header-control rounded p-2">
                <i className="bi bi-file-earmark-spreadsheet text-success" style={{ fontSize: '4rem' }}></i>
              </div>
              <h6 className="mt-3 text-muted">No Google Sheets Integrations</h6>
              <p className="text-muted">Add your first Google Sheets integration to get started</p>
            </div>
          ) : (
            <div className="row">
              {googleSheetsIntegrations.map((integration) => (
                <div key={integration.id} className="col-md-6 mb-3">
                  <Card className="border">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {integration.name || 'Google Sheets Integration'}
                          </h6>
                          <div className="text-muted small mb-1">
                            <i className="bi bi-file-earmark-spreadsheet me-1"></i>
                            Spreadsheet ID: {integration.spreadsheet_id}
                          </div>
                          <div className="text-muted small mb-1">
                            <i className="bi bi-table me-1"></i>
                            Sheet: {integration.sheet_name}
                          </div>
                          <div className="text-muted small mb-1">
                            <i className="bi bi-tag me-1"></i>
                            Type: {integration.type}
                          </div>
                          <div className="text-muted small">
                            <i className="bi bi-calendar-range me-1"></i>
                            {new Date(integration.start_date).toLocaleDateString()} - {new Date(integration.end_date).toLocaleDateString()}
                          </div>
                        </div>
                        <span className={`badge ${
                          integration.connection_status === 'connected' ? 'bg-success' :
                          integration.connection_status === 'error' ? 'bg-danger' : 'bg-secondary'
                        }`}>
                          {integration.connection_status === 'connected' ? 'Connected' :
                           integration.connection_status === 'error' ? 'Error' : 'Not Tested'}
                        </span>
                      </div>

                      <div className="d-flex gap-2 mb-3">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleTestGoogleSheetsConnection(integration.id, integration.name)}
                          disabled={integration.status !== 'active'}
                        >
                          <i className="bi bi-check-circle me-1"></i>
                          Test Connection
                        </Button>
                        <SyncButton
                          claimId={`googlesheets-sync-${integration.id}`}
                          onClick={() => handleSyncGoogleSheetsIntegration(integration.id, integration.name)}
                          disabled={integration.status !== 'active'}
                        />
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteGoogleSheetsIntegration(integration.id, integration.name)}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </Button>
                      </div>

                      {integration.connection_message && (
                        <div className={`alert ${integration.connection_status === 'connected' ? 'alert-success' : 'alert-danger'} alert-sm mb-3`} role="alert">
                          <i className={`bi ${integration.connection_status === 'connected' ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>
                          <small>{integration.connection_message}</small>
                        </div>
                      )}

                      <div className="mb-3 pt-2 border-top">
                        <small className="text-muted d-block">Last Sync:</small>
                        <small>
                          {integration.last_sync_at
                            ? new Date(integration.last_sync_at).toLocaleString()
                            : 'Never'
                          }
                        </small>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add Google Sheets Integration Modal */}
      <Modal show={showAddGoogleSheetsIntegration} onHide={() => {
        setShowAddGoogleSheetsIntegration(false);
        setNewGoogleSheetsName('');
        setNewGoogleSheetsCredentials('');
        setNewGoogleSheetsSpreadsheetId('');
        setNewGoogleSheetsSheetName('');
        setNewGoogleSheetsType('schedule');
        setNewGoogleSheetsStartDate('');
        setNewGoogleSheetsEndDate('');
        setGoogleSheetsError(null);
        setShowGoogleSheetsCredentials(false);
      }} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Google Sheets Integration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {googleSheetsError && (
            <Alert variant="danger" dismissible onClose={() => setGoogleSheetsError(null)}>
              {googleSheetsError}
            </Alert>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Integration Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Sales Schedule 2024"
              value={newGoogleSheetsName}
              onChange={(e) => setNewGoogleSheetsName(e.target.value)}
              maxLength={100}
            />
            <Form.Text className="text-muted">
              Friendly name to identify this integration
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Service Account Credentials (JSON) <span className="text-danger">*</span></Form.Label>
            <div className="input-group">
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Paste your Google Service Account JSON credentials here"
                value={newGoogleSheetsCredentials}
                onChange={(e) => setNewGoogleSheetsCredentials(e.target.value)}
                style={{
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  WebkitTextSecurity: showGoogleSheetsCredentials ? 'none' : 'disc'
                } as React.CSSProperties}
              />
            </div>
            <div className="d-flex justify-content-between align-items-center mt-1">
              <Form.Text className="text-muted">
                <i className="bi bi-shield-lock me-1"></i>
                This will be encrypted and stored securely
              </Form.Text>
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowGoogleSheetsCredentials(!showGoogleSheetsCredentials)}
              >
                <i className={`bi ${showGoogleSheetsCredentials ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                {showGoogleSheetsCredentials ? ' Hide' : ' Show'}
              </Button>
            </div>
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Spreadsheet ID <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 1abc...xyz"
                  value={newGoogleSheetsSpreadsheetId}
                  onChange={(e) => setNewGoogleSheetsSpreadsheetId(e.target.value)}
                  style={{ fontFamily: 'monospace' }}
                />
                <Form.Text className="text-muted">
                  Found in the Google Sheets URL
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Sheet Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Sheet1"
                  value={newGoogleSheetsSheetName}
                  onChange={(e) => setNewGoogleSheetsSheetName(e.target.value)}
                />
                <Form.Text className="text-muted">
                  The specific sheet tab name
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Integration Type <span className="text-danger">*</span></Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                label="Schedule"
                name="googleSheetsType"
                id="type-schedule"
                checked={newGoogleSheetsType === 'schedule'}
                onChange={() => setNewGoogleSheetsType('schedule')}
              />
              <Form.Check
                inline
                type="radio"
                label="AR (Accounts Receivable)"
                name="googleSheetsType"
                id="type-ar"
                checked={newGoogleSheetsType === 'ar'}
                onChange={() => setNewGoogleSheetsType('ar')}
              />
            </div>
            <Form.Text className="text-muted">
              Schedule for employee scheduling data, AR for accounts receivable data
            </Form.Text>
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={newGoogleSheetsStartDate}
                  onChange={(e) => setNewGoogleSheetsStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={newGoogleSheetsEndDate}
                  onChange={(e) => setNewGoogleSheetsEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowAddGoogleSheetsIntegration(false);
              setNewGoogleSheetsName('');
              setNewGoogleSheetsCredentials('');
              setNewGoogleSheetsSpreadsheetId('');
              setNewGoogleSheetsSheetName('');
              setNewGoogleSheetsType('schedule');
              setNewGoogleSheetsStartDate('');
              setNewGoogleSheetsEndDate('');
              setGoogleSheetsError(null);
              setShowGoogleSheetsCredentials(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleCreateGoogleSheetsIntegration}
            disabled={
              isCreatingGoogleSheets ||
              !newGoogleSheetsName.trim() ||
              !newGoogleSheetsCredentials.trim() ||
              !newGoogleSheetsSpreadsheetId.trim() ||
              !newGoogleSheetsSheetName.trim() ||
              !newGoogleSheetsStartDate ||
              !newGoogleSheetsEndDate
            }
          >
            {isCreatingGoogleSheets ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Creating...
              </>
            ) : (
              <>
                <i className="bi bi-plus-circle me-1"></i>
                Create Integration
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GoogleSheetsTab;
